import { isDefined } from '@sgme/fp';
import {
  BatchNavigationContext,
  useBatchNavigationContext,
} from 'components/common-form/contexts/batch/BatchNavigationContext';
import { NumericFieldsContext } from 'components/common-form/contexts/NumericFieldsContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useField } from 'components/common-form/hooks/useField';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { Amount, AmountProps } from 'components/shared/Amount/Amount';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { StyledNumberFormatterProps } from 'components/shared/NumberFormatter/NumberFormatter.model';
import type { PercentProps } from 'components/shared/Percent/model';
import { withIncrementers } from 'components/shared/Percent/WithIncrementers';
import { WithMinMax } from 'components/shared/WithMinMax';
import { WithUnitComponent, type WithUnitProps } from 'components/shared/WithUnit/WithUnit';
import type { IntlKey } from 'locales';
import { ValueType } from 'models/forms/fields/MultipleUnitsFieldMetadata';
import { useContext, useMemo } from 'react';
import { getTextAlignClassName } from 'shared/utils/getTextAlignClassName';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export function MultipleUnitsField({ node: { id }, workspaceType, tabIndex }: ProductNodeBuilderProps) {
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const {
    metadata: {
      units,
      nbDecimals,
      translationKey,
      hasTooltip,
      textAlign,
      minWidth,
      maxWidth,
      isHighlighted,
      tooltipLocation,
      minimum,
      maximum,
      unitsPosition,
    },
    data,
    setData,
  } = useField<'MultipleUnitsField'>(id)!;
  const {
    onEditingKeyDown: onKeyDown,
    lostInputBeforeEditing,
    isCellInactive,
    isFocused,
    isSelectAll,
  } = useContext(BatchNavigationContext);

  const highlightColor = useHighlightColor();
  const numericFieldCtx = useContext(NumericFieldsContext);
  const lostInputValue = useMemo<number | undefined>(() => {
    return lostInputBeforeEditing ? Number(lostInputBeforeEditing) : undefined;
  }, [lostInputBeforeEditing]);
  const { decimalPadding } = useBatchNavigationContext({});
  const props: PercentProps & AmountProps & WithUnitProps = {
    e2e: id,
    precision: nbDecimals,
    decimalPadding: decimalPadding,
    value: lostInputValue ?? data?.value,
    onBlur: (val?: number) => {
      const newValue: Nullable<number> = val ?? null;
      if (newValue !== data?.value) {
        setData(isDefined(data) ? { ...data, value: newValue, changeTarget: 'value' } : null);
      }
    },
    isDisabled: workspaceType === 'newBatch' ? isReadOnly : false,
    isReadOnly,
  };

  const tooltipKey = hasTooltip ? (`form.${translationKey}.tooltip` as IntlKey) : undefined;
  const textAlignClassName = getTextAlignClassName(textAlign);

  const WithIncrementersComponent = numericFieldCtx.useIncrementers
    ? withIncrementers(Amount, {
        layout: numericFieldCtx.incrementersLayout,
      })
    : Amount;
  const unit = data?.valueType && units[data?.valueType];

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      minWidth={minWidth}
      maxWidth={maxWidth}
      tooltipLocation={tooltipLocation}
      tooltipKey={tooltipKey}
      className={patchedFieldClassName}
    >
      <WithMinMax
        id={id}
        minimum={minimum}
        maximum={maximum}
        unitsPosition={unitsPosition}
        Component={WithUnitComponent<StyledNumberFormatterProps>}
        UnitComponent={WithIncrementersComponent}
        units={Object.values(units)}
        unit={unit}
        unitsDirection="down"
        onUnitSelected={u => {
          const valueType = Object.entries(units).find(([, v]) => v === u)?.[0];
          if (valueType) {
            setData({
              value: null,
              valueType: valueType as ValueType,
              changeTarget: 'valueType',
            });
          }
        }}
        {...props}
        hasError={isFieldInvalid}
        isHighlighted={isHighlighted ? highlightColor : undefined}
        className={textAlignClassName}
        tabIndex={tabIndex}
        isCellInactive={isCellInactive}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
      />
    </BlockOrientationFormGroup>
  );
}
